// Вкладка «ABC-анализ» раздела «Себестоимость» (docs/tasks/task-menu-abc.md). Данные — /menu_abc (грузятся,
// когда вкладку открыли; CostPage держит её смонтированной), расчёт — buildMenuAbc. Фильтры: общий выбор
// ресторана и период; категория — внутри вкладки. Список блюд: единый фильтр (рекомендация, группа, клетка матрицы),
// поиск, сортировка, первые 30 (abcList). Смена ресторана или категории сбрасывает фильтр, поиск и «Показать все»
// (сортировка остаётся); смена периода фильтр сохраняет, но если в нём стало 0 блюд — сбрасывает.
// Динамика (от недели): второй запрос /menu_abc за период сравнения (trendPeriod.comparisonFor), сравнение —
// abcDynamics.compareAbc; данные сравнения привязаны к своему периоду — чужие не подставляются.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, ButtonBase, ClickAwayListener, Menu, MenuItem, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PeriodSelector from '../../components/PeriodSelector';
import FiltersTopRow from '../../components/FiltersTopRow';
import StickyFiltersBar from '../../components/StickyFiltersBar';
import NoReportAccess from '../../components/NoReportAccess';
import { useDesktopLayout } from '../../components/layout/AppLayout';
import GroupSelector from '../Metrics/components/GroupSelector';
import { useBusinessDay } from '../../hooks/useBusinessDay';
import { fetchMenuAbcData } from '../../services/dataService';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS, dimSx } from '../../styles/tokens';
import { extractErrorMessage, isReportAccessDenied } from '../../utils/errors';
import { formatPeriodShort, getPeriodRange } from '../../utils/periods';
import { ALL, buildMenuAbc } from './utils/abcCalculations';
import { DEFAULT_SORT, countMatching } from './utils/abcList';
import { compareAbc } from './utils/abcDynamics';
import { comparisonFor, comparisonKey } from '../Metrics/utils/trendPeriod';
import { AbcChangesCard, AbcGoneList } from './components/abc/AbcChanges';
import AbcDistributionCard from './components/abc/AbcDistributionCard';
import AbcMatrixCard from './components/abc/AbcMatrixCard';
import AbcRecommendations from './components/abc/AbcRecommendations';
import AbcDishes from './components/abc/AbcDishes';
import { plural } from './components/abc/abcLook';

const ACCENT = SECTION_ACCENTS['/cost'];
const FEW_DISHES = 5;
const SHORT_PERIODS = ['today', 'yesterday'];
// Динамика — только от недели: короткие периоды слишком шумные для сравнения
const DYNAMIC_PERIODS = ['week', 'month', 'lastWeek', 'lastMonth'];
const INFO_TEXT =
  'Блюда сортируются по показателю, A — дают первые 80% результата, B — следующие 15%, C — последние 5%. ' +
  'Три буквы у блюда: продажи в штуках, выручка, прибыль. ' +
  'Динамика (от недели): сдвиг группы по прибыли к прошлому периоду той же длины. ' +
  'Модификаторы и услуги не учитываются.';

// «i»: на десктопе — по наведению, на телефоне — по нажатию (закрывается нажатием мимо)
function InfoHint() {
  const [open, setOpen] = useState(false);
  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <span>
        <Tooltip title={INFO_TEXT} open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} arrow>
          <ButtonBase
            onClick={() => setOpen((v) => !v)}
            aria-label="Как считается ABC-анализ"
            sx={{ width: 44, height: 44, borderRadius: '50%', color: MUTED_TEXT_COLOR, '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}` } }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </ButtonBase>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
}

function CategoryPicker({ categories, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          minHeight: 44,
          px: 1.5,
          gap: 0.5,
          borderRadius: '10px',
          maxWidth: '100%',
          fontSize: '14px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: 'rgba(255,255,255,0.06)',
          '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' },
        }}
      >
        <Box component="span" sx={{ color: MUTED_TEXT_COLOR, fontWeight: 500 }}>Категория:</Box>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value === ALL ? 'Все' : value}</Box>
        <KeyboardArrowDownIcon fontSize="small" aria-hidden="true" />
      </ButtonBase>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { maxHeight: 420 } } }}>
        {[ALL, ...categories].map((category) => (
          <MenuItem
            key={category}
            selected={category === value}
            onClick={() => {
              onChange(category);
              setAnchor(null);
            }}
            sx={{ minHeight: 44 }}
          >
            {category === ALL ? 'Все категории' : category}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function Hint({ children, action }) {
  return (
    <Box role="status" sx={{ ...CARD_SURFACE_SX, px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>{children}</Typography>
      {action}
    </Box>
  );
}

function LoadingSkeleton({ desktop }) {
  return (
    <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Skeleton variant="rounded" height={150} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Skeleton variant="rounded" height={desktop ? 300 : 60} />
        <Skeleton variant="rounded" height={desktop ? 300 : 60} />
      </Box>
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={48} />)}
    </Box>
  );
}

export default function AbcTab({ selectedGroup, allGroups, activeServerName, setSelectedGroup, period, onPeriodChange, tabs = null }) {
  const desktop = useDesktopLayout();
  const businessDay = useBusinessDay();
  const dateRange = useMemo(() => (businessDay ? getPeriodRange(period, businessDay) : null), [period, businessDay]);
  const selection = selectedGroup || ALL;
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [category, setCategory] = useState(ALL);
  const [filter, setFilter] = useState(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [showAll, setShowAll] = useState(false);
  const [goneOpen, setGoneOpen] = useState(false);
  const [previous, setPrevious] = useState(null); // { key, rows } — данные периода сравнения
  const comparison = useMemo(
    () => (DYNAMIC_PERIODS.includes(period) ? comparisonFor(period, dateRange) : null),
    [period, dateRange],
  );
  const compareKey = comparisonKey(comparison);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !dateRange?.from || !dateRange?.to) return undefined;
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      try {
        const server = JSON.parse(localStorage.getItem('activeServer') || 'null');
        if (!server) throw new Error('Сервер не найден');
        const data = await fetchMenuAbcData(token, server, dateRange);
        if (alive) setRows(Array.isArray(data?.data) ? data.data : []);
      } catch (err) {
        if (!alive) return;
        if (isReportAccessDenied(err)) setAccessDenied(true);
        else setError(extractErrorMessage(err) || 'Ошибка при загрузке ABC-анализа');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dateRange]);

  // Данные сравнения — отдельным запросом, со своим ключом периода
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !comparison) return undefined;
    let alive = true;
    const server = JSON.parse(localStorage.getItem('activeServer') || 'null');
    if (!server) return undefined;
    fetchMenuAbcData(token, server, comparison)
      .then((data) => alive && setPrevious({ key: compareKey, rows: Array.isArray(data?.data) ? data.data : [] }))
      // Сравнение не загрузилось — без карточки динамики (основной анализ работает)
      .catch(() => alive && setPrevious({ key: compareKey, rows: null, failed: true }));
    return () => {
      alive = false;
    };
  }, [compareKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = useMemo(() => buildMenuAbc(rows || [], selection, category), [rows, selection, category]);
  const previousRows = previous && previous.key === compareKey ? previous.rows : null;
  const dynamics = useMemo(
    () => (previousRows ? compareAbc(result, buildMenuAbc(previousRows, selection, category)) : null),
    [result, previousRows, selection, category],
  );
  // Блюда с их сдвигом к прошлому периоду (для меток и фильтра «Упали / Выросли / Новые»)
  const dishes = useMemo(
    () => result.dishes.map((dish) => ({ ...dish, change: dynamics?.changes.get(dish.key) || null })),
    [result.dishes, dynamics],
  );

  // Категория пропала (другой период или ресторан) — «Все»
  useEffect(() => {
    if (rows && category !== ALL && !result.categories.includes(category)) setCategory(ALL);
  }, [rows, category, result.categories]);

  // Другой ресторан или категория — список заново: без фильтра, поиска и «Показать все» (сортировка остаётся)
  const scopeRef = useRef(`${selection}|${category}`);
  useEffect(() => {
    const scope = `${selection}|${category}`;
    if (scopeRef.current === scope) return;
    scopeRef.current = scope;
    setFilter(null);
    setQuery('');
    setShowAll(false);
    setGoneOpen(false);
  }, [selection, category]);

  // В фильтре не осталось блюд (другой период) — снимаем
  useEffect(() => {
    if (filter && countMatching(dishes, filter) === 0) setFilter(null);
  }, [filter, dishes]);
  const skeleton = (loading || !businessDay) && !rows;
  const dimmed = loading && Boolean(rows);
  const empty = rows && result.dishes.length === 0;

  let body;
  if (accessDenied) body = <NoReportAccess />;
  else if (error && !rows) body = <Hint>{error}</Hint>;
  else if (skeleton) body = <LoadingSkeleton desktop={desktop} />;
  else if (empty) {
    body = (
      <>
        <Hint>За этот период нет продаж по выбранному ресторану и категории</Hint>
        {result.excluded > 0 && (
          <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            {`Не учтено ${result.excluded} ${plural(result.excluded, ['позиция', 'позиции', 'позиций'])} с нулевой или отрицательной выручкой`}
          </Typography>
        )}
      </>
    );
  } else {
    body = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...dimSx(dimmed) }}>
        {error && <Hint>{error}</Hint>}
        {SHORT_PERIODS.includes(period) && (
          <Hint
            action={
              <Button onClick={() => onPeriodChange('month')} sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600, color: ACCENT.tab }}>
                Показать за месяц
              </Button>
            }
          >
            За день данных мало для анализа
          </Hint>
        )}
        {result.dishes.length < FEW_DISHES && (
          <Hint>{`Блюд в анализе: ${result.dishes.length}. На малом числе блюд разбивка на группы малоинформативна`}</Hint>
        )}
        <AbcDistributionCard result={result} filter={filter} onFilter={setFilter} />
        {comparison && !(previous?.key === compareKey && previous.failed) && (
          <AbcChangesCard
            counts={dynamics?.counts}
            label={comparison.label}
            loading={!dynamics}
            filter={filter}
            onFilter={setFilter}
            goneOpen={goneOpen}
            onToggleGone={() => setGoneOpen((v) => !v)}
          />
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.2fr) minmax(0, 1fr)' }, gap: 2, alignItems: { xs: 'start', md: 'stretch' } }}>
          <AbcMatrixCard matrix={result.matrix} desktop={desktop} filter={filter} onFilter={setFilter} />
          <AbcRecommendations tagCounts={result.tagCounts} filter={filter} onFilter={setFilter} desktop={desktop} />
        </Box>
        <AbcDishes
          dishes={dishes}
          filter={filter}
          onFilter={setFilter}
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          showAll={showAll}
          onShowAll={() => setShowAll(true)}
          excluded={result.excluded}
          desktop={desktop}
        />
        {comparison && dynamics && (
          <AbcGoneList gone={dynamics.gone} open={goneOpen} onToggle={() => setGoneOpen((v) => !v)} label={comparison.label} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#fff', width: '100%' }}>
      <StickyFiltersBar>
        <FiltersTopRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, maxWidth: '100%' }}>
            {allGroups && (
              <GroupSelector
                groups={allGroups}
                selectedGroup={selection}
                onSelectGroup={setSelectedGroup}
                activeServerName={activeServerName}
                sx={{ mb: 0 }}
              />
            )}
            {desktop && tabs}
          </Box>
        </FiltersTopRow>
        {!desktop && tabs}
        <PeriodSelector period={period} rangeLabel={formatPeriodShort(dateRange)} onChange={onPeriodChange} showFilters={false} />
      </StickyFiltersBar>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <CategoryPicker categories={result.categories} value={category} onChange={setCategory} />
          <InfoHint />
        </Box>
        {body}
      </Box>
    </Box>
  );
}
